import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../api/client';
import type { Automation, AutomationTypeInfo, BucketInfo, RetentionConfig } from '../api/types';
import { INTERVAL_PRESETS, intervalLabel, ruleSummary, StatusPill, ToggleSwitch } from '../components/automation-ui';

const inputCls =
  'w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy';
const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500';

function CreateForm({
  types,
  buckets,
  onClose,
  onCreated,
}: {
  types: AutomationTypeInfo[];
  buckets: BucketInfo[];
  onClose: () => void;
  onCreated: (created: Automation) => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState(types[0]?.name ?? 'retention-cleanup');
  const [bucket, setBucket] = useState(buckets[0]?.logicalName ?? '');
  const [pattern, setPattern] = useState('');
  const [olderThanDays, setOlderThanDays] = useState('');
  const [keepNewest, setKeepNewest] = useState('');
  const [maxDeletes, setMaxDeletes] = useState('');
  const [intervalMinutes, setIntervalMinutes] = useState(1440);

  const create = useMutation({
    mutationFn: () => {
      const config: RetentionConfig = {
        bucket,
        pattern: pattern.trim(),
        ...(olderThanDays.trim() ? { olderThanDays: Number(olderThanDays) } : {}),
        ...(keepNewest.trim() ? { keepNewest: Number(keepNewest) } : {}),
        ...(maxDeletes.trim() ? { maxDeletesPerRun: Number(maxDeletes) } : {}),
      };
      return api<Automation>('/api/admin/automations', {
        method: 'POST',
        body: JSON.stringify({ name, type, config, intervalMinutes }),
      });
    },
    onSuccess: (created) => onCreated(created),
  });

  const canCreate = !!name.trim() && !!bucket && !!pattern.trim() && (!!olderThanDays.trim() || !!keepNewest.trim());
  const selectedType = types.find((t) => t.name === type);

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-navy">New automation</h2>

      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Name</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. odincli release cleanup" />
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
              {types.map((t) => (
                <option key={t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
            {selectedType ? <p className="mt-1 text-xs text-gray-500">{selectedType.description}</p> : null}
          </div>
          <div>
            <label className={labelCls}>Runs</label>
            <select className={inputCls} value={intervalMinutes} onChange={(e) => setIntervalMinutes(Number(e.target.value))}>
              {INTERVAL_PRESETS.map((p) => (
                <option key={p.minutes} value={p.minutes}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Bucket</label>
              <select className={inputCls} value={bucket} onChange={(e) => setBucket(e.target.value)}>
                {buckets.map((b) => (
                  <option key={b.logicalName} value={b.logicalName}>{b.logicalName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Path pattern</label>
              <input
                className={`${inputCls} font-mono`}
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                placeholder="releases/odincli/**"
                spellCheck={false}
              />
              <p className="mt-1 text-xs text-gray-500">
                Patterns are explicit: <span className="font-mono">sql/**</span> = everything under{' '}
                <span className="font-mono">sql</span>, <span className="font-mono">sql/*</span> = its direct files only,{' '}
                <span className="font-mono">sql/**/*.bak</span> = .bak files at any depth. Must start with a literal
                folder — whole-bucket cleanup is not allowed. No trailing <span className="font-mono">/</span>.
              </p>
            </div>
          </div>

          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Delete older than (days)</label>
              <input type="number" min={1} className={inputCls} value={olderThanDays} onChange={(e) => setOlderThanDays(e.target.value)} placeholder="e.g. 30" />
            </div>
            <div>
              <label className={labelCls}>Always keep newest</label>
              <input type="number" min={1} className={inputCls} value={keepNewest} onChange={(e) => setKeepNewest(e.target.value)} placeholder="e.g. 10" />
            </div>
            <div>
              <label className={labelCls}>Max deletions per run</label>
              <input type="number" min={1} className={inputCls} value={maxDeletes} onChange={(e) => setMaxDeletes(e.target.value)} placeholder="500" />
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            At least one of age / keep-newest is required. Keep-newest always wins: the newest N matching files are
            never deleted, even when older than the age cutoff.
          </p>
        </div>
      </div>

      {create.isError ? <p className="mt-3 text-sm text-red-600">{getErrorMessage(create.error)}</p> : null}

      <div className="mt-4 flex gap-2">
        <button
          className="rounded bg-navy px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
          disabled={!canCreate || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
        <button className="rounded border border-gray-300 px-4 py-1.5 text-sm hover:bg-gray-50" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function Automations() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const automations = useQuery({ queryKey: ['automations'], queryFn: () => api<{ automations: Automation[] }>('/api/admin/automations') });
  const types = useQuery({ queryKey: ['automation-types'], queryFn: () => api<{ types: AutomationTypeInfo[] }>('/api/admin/automations/types') });
  const buckets = useQuery({ queryKey: ['buckets'], queryFn: () => api<{ buckets: BucketInfo[] }>('/api/admin/buckets') });
  const [showCreate, setShowCreate] = useState(false);

  // Optimistic toggle: flip the row in the cached list immediately, roll back on error.
  const toggle = useMutation({
    mutationFn: (a: Automation) =>
      api(`/api/admin/automations/${a.id}`, { method: 'PATCH', body: JSON.stringify({ enabled: !a.enabled }) }),
    onMutate: async (a) => {
      await qc.cancelQueries({ queryKey: ['automations'] });
      const prev = qc.getQueryData<{ automations: Automation[] }>(['automations']);
      if (prev) {
        qc.setQueryData(['automations'], {
          automations: prev.automations.map((x) => (x.id === a.id ? { ...x, enabled: !x.enabled } : x)),
        });
      }
      return { prev };
    },
    onError: (_err, _a, ctx) => {
      if (ctx?.prev) qc.setQueryData(['automations'], ctx.prev);
    },
    onSettled: (_res, _err, a) => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      void qc.invalidateQueries({ queryKey: ['automation', a.id] });
    },
  });

  const rows = automations.data?.automations ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-navy">Automations</h1>
        <button className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white" onClick={() => setShowCreate(true)}>
          New automation
        </button>
      </div>

      {showCreate ? (
        <CreateForm
          types={types.data?.types ?? []}
          buckets={buckets.data?.buckets ?? []}
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setShowCreate(false);
            void qc.invalidateQueries({ queryKey: ['automations'] });
            // Land on the new automation's page: preview it there, then enable it.
            void navigate(`/automations/${created.id}`);
          }}
        />
      ) : null}

      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <th className="p-2">Name</th>
              <th className="p-2">Target</th>
              <th className="p-2">Rule</th>
              <th className="p-2">Runs</th>
              <th className="p-2">Last run</th>
              <th className="p-2">Enabled</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const cfg = a.config as RetentionConfig;
              return (
                <tr
                  key={a.id}
                  className="cursor-pointer border-b hover:bg-gray-50"
                  onClick={() => void navigate(`/automations/${a.id}`)}
                >
                  <td className="p-2">
                    {a.name}
                    <div className="text-xs text-gray-400">{a.type}</div>
                  </td>
                  <td className="p-2 font-mono text-xs text-gray-600">
                    {cfg.bucket ?? '?'}<span className="text-gray-400"> / </span>{cfg.pattern ?? '?'}
                  </td>
                  <td className="p-2 text-xs text-gray-600">{ruleSummary(a)}</td>
                  <td className="p-2 text-xs text-gray-600">{intervalLabel(a.interval_minutes)}</td>
                  <td className="p-2 text-xs" title={a.last_error ?? undefined}>
                    <StatusPill status={a.last_status} />
                    {a.last_run_utc ? <span className="ml-1.5 text-gray-500">{new Date(a.last_run_utc).toLocaleString()}</span> : null}
                  </td>
                  <td className="p-2">
                    <ToggleSwitch checked={a.enabled} onChange={() => toggle.mutate(a)} />
                  </td>
                  <td className="p-2 text-right text-xs text-gray-400">›</td>
                </tr>
              );
            })}
            {automations.data && rows.length === 0 ? (
              <tr>
                <td className="p-3 text-sm text-gray-500" colSpan={7}>
                  No automations yet. Create one to clean up old files automatically.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {automations.isLoading ? <div className="p-3 text-sm">Loading…</div> : null}
        {automations.isError ? <div className="p-3 text-sm text-red-600">{getErrorMessage(automations.error)}</div> : null}
      </div>
    </div>
  );
}
